<!-- eslint-disable max-lines -->
<template>
	<ValidationForm
		#default="{ handleSubmit, reset, invalid }"
		tag="div"
	>
		<ViewLayout class="dataSubject">
			<template #header-title>
				{{ title }}
			</template>
			<template #header-caption>
				{{ caption }}
			</template>
			<template #header-after>
				<Tabs
					:tab.sync="tab"
					:tabs="tabs"
					:disabled="editMode"
				/>
			</template>
			<template #content>
				<SectionCard v-if="!editMode && isPartOfGroup">
					<template #title>
						Profiles
					</template>
					<template #body>
						<DataTable
							:headers="profilesDataHeaders"
							:items="profilesData"
							:selected="(row) => row.syrenisId === syrenisId"
							hide-default-footer
							@click:row="selectProfile"
						/>
					</template>
				</SectionCard>
				<TabItems
					:tab="tab"
					:tabs="tabs"
				>
					<template #preferences>
						<DataSubjectPreferencesEdit
							v-if="editPreferences"
							ref="dataSubjectPreferencesEdit"
							:data-subject="currentStandDak"
							:preference-groups="preferenceGroupsMappedToEdit"
							:has-email="!!currentStandDak.email1"
							:brand-id="brandId"
							@saved="loadDataSubjects"
							@close="editPreferences = false"
							@invalid-selection="editPrefsIsInvalidSelection"
						/>
						<DataSubjectPreferences
							v-else
							:preference-groups="preferenceGroupsMappedToView"
							:get-current-selection="getCurrentSelection"
							:profiles-by-channel-id="profilesByChannelId"
							:syrenis-id="syrenisId"
							:can-edit-preferences="canEditPreferences"
							:is-part-of-group="isPartOfGroup"
							:brand-id="brandId"
							:data-subject-preferences="dataSubjectPreferences"
							@edit="editPreferences = true"
							@exit="goToDataSubjectSearch"
						/>
					</template>
					<template #contact-details>
						<div>
							<div class="d-flex justify-space-between mb-4 mt-1">
								<DataSubjectBackButton @click="goToDataSubjectSearch" />
								<SecondaryActionButton
									v-if="!editContactDetails && masterCanEditContactDetails"
									:disabled="!canEditContactDetails"
									@click="onEditContactDetails(reset)"
								>
									<v-icon
										left
										dark
									>
										mdi-pencil
									</v-icon>
									Edit Contact Details
								</SecondaryActionButton>
							</div>
							<DataSubjectContactDetails
								v-if="selectedDataSubject"
								:data-subject="editDataSubject || currentStandDak"
								:edit-contact-details="editContactDetails"
								:view-data-subject="!editContactDetails"
								@update:dataSubject="editDataSubject = $event"
							/>
						</div>
					</template>
					<template
						v-if="canSeePreferences"
						#preference-history
					>
						<DataSubjectPreferenceHistory
							:syrenis-id="syrenisId"
							:search-brand-id="brandId"
							@exit="goToDataSubjectSearch"
						/>
					</template>
					<template #submission-history>
						<DataSubjectAuditHistory
							v-bind="selectedDataSubject"
							:get-current-selection="getCurrentSelection"
							:can-see-preferences="canSeePreferences"
							:can-see-contact-details="canSeeContactDetails"
							:syrenis-id="syrenisId"
							:grouped-profile-ids="groupedProfileIds"
							:data-subject-preferences="dataSubjectPreferences"
							@exit="goToDataSubjectSearch"
						/>
					</template>
					<template	#associated-devices>
						<DataSubjectAssociatedDevices
							:selected-profile="{ syrenisId: syrenisId, devices: selectedDataSubject.deviceAssociation }"
							:search-brand-id="brandId"
							@exit="goToDataSubjectSearch"
						/>
					</template>
				</TabItems>
			</template>
			<template
				v-if="showManageFooter"
				#footer
			>
				<div
					v-if="editContactDetails && masterCanEditContactDetails"
					class="d-flex"
				>
					<v-spacer />
					<SecondaryActionButton
						@click="onCancelEditContactDetails(reset)"
					>
						Cancel
					</SecondaryActionButton>
					<PrimaryActionButton
						class="ml-4"
						@click="handleSubmit(onSaveContactDetails)"
					>
						Save
					</PrimaryActionButton>
				</div>
				<div
					v-else
					class="d-flex"
				>
					<v-spacer />
					<SecondaryActionButton @click="$refs.dataSubjectPreferencesEdit.close(reset)">
						Cancel
					</SecondaryActionButton>
					<PrimaryActionButton
						class="ml-4"
						:disabled="isInvalidSelection"
						@click="handleSubmit(submitPreferences, invalid)"
					>
						Save
					</PrimaryActionButton>
				</div>
			</template>
			<template
				v-else
				#footer
			>
				<div
					class="d-flex"
				>
					<v-spacer />
					<PrimaryActionButton
						@click="exportToPdf"
					>
						Export to PDF
					</PrimaryActionButton>
				</div>
			</template>
		</ViewLayout>
	</ValidationForm>
</template>

<script>
/* eslint-disable max-lines */
import { mapGetters } from 'vuex'
import { format } from 'date-fns'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import SyrenisLogoBlack from '../../../../assets/images/syrenis-black.png'
import ViewLayout from '../../../../../../shared/layouts/view-layout.vue'
import Tabs from '../../../../../../shared/components/tabs.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import ValidationForm from '../../../../../../shared/components/validation-form.vue'
import {
	getDataSubject,
	getDataSubjectAssociations,
	postDataSubject,
	getDataSubjectHistory,
	getDataSubjectSubmissions
} from '../../../../../../shared/utils/api/data-subject.js'
import { DATE_TIME_FORMAT, formatUtcDate } from '../../../../../../shared/utils/date-formatting.js'
import {
	contactDetailsFields,
	dataSubjectSearchFields,
	dataSubjectSearchResults,
	masterCanEditContactDetails,
	profileTableFields
	, preferenceGroups
} from '../../../../../../shared/state/configuration.js'
import { DATA_SUBJECT_SEARCH } from '../../../../router/route-names.js'
import DataSubjectPreferences from './data-subject-preferences/data-subject-preferences.vue'
import DataSubjectContactDetails from '../data-subject-contact-details.vue'
import DataSubjectAuditHistory from './data-subject-audit-history.vue'
import DataSubjectPreferenceHistory from './data-subject-preference-history.vue'
import DataSubjectPreferencesEdit from './data-subject-preferences/data-subject-preferences-edit.vue'
import { NO_PREFERENCE, OPTED_IN, OPTED_OUT } from './data-subject-constants.js'
import DataSubjectBackButton from './data-subject-back-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import {
	DATA_SUBJECT_MANAGEMENT_AREA_FULL_PERMISSIONS,
	EDIT_DATA_SUBJECT_CONTACT_DETAILS,
	EDIT_DATA_SUBJECT_CURRENT_PREFERENCES,
	SEE_DATA_SUBJECT_CONTACT_DETAILS,
	SEE_DATA_SUBJECT_CURRENT_PREFERENCES,
	SEE_DATA_SUBJECT_PREFERENCE_HISTORY,
	SEE_DATA_SUBJECT_SUBMISSION_HISTORY
} from '../../../../../../shared/permissions/cs-portal-permissions.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import TabItems from '../../../../../../shared/components/tab-items.vue'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import { NO_BRAND_SELECTED, UNIVERSAL_BRAND, csPortalBrandSelector } from '../../../../../../shared/state/brands.js'
import DataSubjectAssociatedDevices from './data-subject-associated-devices.vue'
export default {
	components: {
		SectionCard,
		TabItems,
		PrimaryActionButton,
		SecondaryActionButton,
		DataSubjectBackButton,
		DataSubjectPreferencesEdit,
		DataTable,
		Tabs,
		DataSubjectAuditHistory,
		DataSubjectPreferenceHistory,
		DataSubjectContactDetails,
		DataSubjectPreferences,
		ViewLayout,
		ValidationForm,
		DataSubjectAssociatedDevices
	},
	props: {
		id: {
			type: Number,
			required: true
		},
		brandId: {
			type: Number,
			default: NO_BRAND_SELECTED
		},
		allIds: {
			type: Array,
			default: () => []
		}
	},
	setup () {
		return {
			contactDetailsFields,
			dataSubjectSearchFields,
			dataSubjectSearchResults,
			masterCanEditContactDetails,
			showSnackbar,
			profileTableFields,
			preferenceGroups,
			UNIVERSAL_BRAND,
			csPortalBrandSelector
		}
	},
	data () {
		return {
			tab: 'preferences',
			dataSubjects: [],
			syrenisId: this.id,
			editPreferences: false,
			editContactDetails: false,
			NO_PREFERENCE,
			editDataSubject: null,
			isInvalidSelection: false,
			groupedProfileIds: [],
			historicPreferences: null,
			submissionHistory: null
		}
	},
	computed: {
		...mapGetters('auth', ['hasPermission']),
		tabs () {
			const tabsArray = []
			if (this.canSeePreferences) {
				tabsArray.push({ title: 'Preferences', slot: 'preferences', icon: 'mdi-order-bool-ascending-variant' })
			}
			if (this.canSeeContactDetails) {
				tabsArray.push({ title: 'Contact Details', slot: 'contact-details', icon: 'mdi-card-account-mail-outline' })
			}
			if (this.canSeePreferenceHistory && this.canSeePreferences) {
				tabsArray.push({
					title: 'Customer History',
					slot: 'preference-history',
					icon: 'mdi-history'
				})
			}
			if (this.canSeeSubmissionHistory) {
				tabsArray.push({
					title: 'Submission History',
					slot: 'submission-history',
					icon: 'mdi-history'
				})
			}
			if (this.hasAssociatedDevices) {
				tabsArray.push({
					title: 'Associated Devices',
					slot: 'associated-devices',
					icon: 'mdi-cellphone-link'
				})
			}
			return tabsArray
		},
		editMode () {
			return this.editPreferences || this.editContactDetails
		},
		title () {
			return `${this.currentStandDak?.forename || ''} ${
				this.currentStandDak?.surname || ''
			}`
		},
		caption () {
			return this.contactDetailsFields
				.filter(field => field.showInOverview)
				.map(({ fieldName }) => this.currentStandDak[fieldName])
				.join(', ')
		},
		currentStandDak () {
			return this.selectedDataSubject?.currentStandDak || {}
		},
		isPartOfGroup () {
			return this.dataSubjects.length > 1
		},
		selectedDataSubject () {
			return this.dataSubjects.find(
				({ currentStandDak: { syrenisId } }) => syrenisId === this.syrenisId
			)
		},
		profilesData () {
			return this.dataSubjects.map(({ currentStandDak }) => currentStandDak)
		},
		profilesDataHeaders () {
			return this.dataSubjectSearchResults
				.filter(field => field.includeInSearchResults)
				.map(({ fieldName, fieldLabel }) => ({
					text: fieldLabel || fieldName,
					value: fieldName
				}))
		},
		dataSubjectPreferences () {
			return this.getDataSubjectPreferences(this.selectedDataSubject)
		},
		preferenceGroupsMapped () {
			const result = this.preferenceGroups.map(group => ({
				...group,
				subGroups: group.subGroups.sort((a, b) => a.channelID > b.channelID)
					.map(subGroup => ({
						...subGroup,
						channels: subGroup.channels
							.sort((a, b) => a.displayOrderInSubGroup > b.displayOrderInSubGroup)
							.filter(channel => channel.display)
							.map(channel => {
								const preferenceChannel = this.dataSubjectPreferences?.find(x => x.channelId === channel.channelId)
								return {
									...channel,
									...preferenceChannel,
									amendDateFrom: false,
									hasPreference: true,
									canBeAdded: channel.canBeAdded,
									brandName: preferenceChannel != null ? preferenceChannel.brandName : channel.brandName,
									brandID: preferenceChannel != null ? preferenceChannel.brandID : channel.brandId,
									channelId: preferenceChannel != null ? preferenceChannel.channelId : channel.channelId,
									channelName: preferenceChannel != null ? preferenceChannel.channelName : channel.channelName,
									privacyPolicyUrl: preferenceChannel != null ? preferenceChannel.privacyPolicyURL : channel.privacyPolicyURL,
									privacyPolicyName: preferenceChannel != null ? preferenceChannel.privacyPolicyName : channel.privacyPolicyName,
									privacyPolicyVersion: preferenceChannel != null ? preferenceChannel.privacyPolicyVersion : channel.privacyPolicyVersion,
									privacyId: preferenceChannel != null ? preferenceChannel.privacyID : channel.privacyID,
									responseChannel: preferenceChannel != null ? preferenceChannel.responseChannel : channel.responseChannel,
									statementText: preferenceChannel != null ? preferenceChannel.statementText : channel.statementText,
									divId: preferenceChannel != null ? preferenceChannel.divID : channel.divID,
									dateSubmitted: preferenceChannel != null
										? format(
											new Date(preferenceChannel.dateSubmitted),
											DATE_TIME_FORMAT
										)
										: null,
									sourceCode: preferenceChannel != null ? preferenceChannel.sourceCode : channel.sourceCode,
									isOptIn: preferenceChannel != null ? preferenceChannel.isOptIn : channel.isOptIn,
									isChannelOptedIn: preferenceChannel != null ? preferenceChannel.isChannelOptedIn : channel.isChannelOptedIn,
									currentSelection: preferenceChannel != null ? preferenceChannel?.isChannelOptedIn ? OPTED_IN : OPTED_OUT : NO_PREFERENCE,
									validTo: preferenceChannel?.preferenceValidTo
										? format(
											new Date(preferenceChannel.preferenceValidTo),
											DATE_TIME_FORMAT)
										: null,
									validFrom: preferenceChannel?.preferenceValidFrom
										? format(
											new Date(preferenceChannel.preferenceValidFrom),
											DATE_TIME_FORMAT
										)
										: preferenceChannel?.consentDate
											? format(
												new Date(preferenceChannel.consentDate),
												DATE_TIME_FORMAT
											)
											: null,
									channelExtendedPrefs: channel?.extendedPrefs.filter(({ hideFromUi }) => hideFromUi === false) ?? [],
									extendedPrefs: preferenceChannel?.extendedPrefs.filter(({ hideFromUi }) => hideFromUi === false) ?? [],
									mergedExtendedPrefs: channel.extendedPrefs
										.map(channelExtPrefs => ({
											...channelExtPrefs,
											...preferenceChannel?.extendedPrefs.find(
												extPrefs => extPrefs.key === channelExtPrefs.keyName
											)
										}))
										.filter(({ hideFromUi }) => hideFromUi === false)
								}
							})
					})).filter(subGroup => subGroup.channels.length > 0)
			}))
			return result.sort((a, b) => a.displayOrder - b.displayOrder).filter(group => group.subGroups.flat().length > 0)
		},
		preferenceGroupsMappedToView () {
			const userBrandValues = csPortalBrandSelector.value.map(brand => {
				return brand.value
			})
			const result = this.preferenceGroupsMapped.map(group => ({
				...group,
				subGroups: group.subGroups.map(subGroup => ({
					...subGroup,
					channels: this.brandId !== UNIVERSAL_BRAND
						? subGroup.channels.filter(channel => channel.currentSelection !== NO_PREFERENCE || !channel.hideNoPref)
							.filter(pref => this.dataSubjectPreferences?.find(x => x.channelId === pref.channelId) || !pref.hideNoPref)
						: subGroup.channels.filter(channel => channel.currentSelection !== NO_PREFERENCE || !channel.hideNoPref && userBrandValues.includes(channel.brandId))
							.filter(pref => this.dataSubjectPreferences?.find(x => x.channelId === pref.channelId) || !pref.hideNoPref)
				})).filter(subGroup => subGroup.channels?.length > 0)
			})
			).filter(group => group.subGroups?.length > 0)
			return result
		},
		preferenceGroupsMappedToEdit () {
			const userBrandValues = csPortalBrandSelector.value.map(brand => {
				return brand.value
			})
			const result = this.preferenceGroupsMapped.map(group => ({
				...group,
				subGroups: group.subGroups.map(subGroup => ({
					...subGroup,
					channels: this.brandId !== UNIVERSAL_BRAND
						? subGroup.channels
						: subGroup.channels.filter(channel => userBrandValues.includes(channel.brandId))
				})).filter(subGroup => subGroup.channels?.length > 0)
			})
			).filter(group => group.subGroups?.length > 0)
			return result
		},
		hasFullPermissions () {
			return this.hasPermission(DATA_SUBJECT_MANAGEMENT_AREA_FULL_PERMISSIONS)
		},
		canSeePreferences () {
			if (this.hasFullPermissions) {
				return true
			}
			return this.hasPermission(SEE_DATA_SUBJECT_CURRENT_PREFERENCES)
		},
		canSeeContactDetails () {
			if (this.hasFullPermissions) {
				return true
			}
			return this.hasPermission(SEE_DATA_SUBJECT_CONTACT_DETAILS)
		},
		canSeePreferenceHistory () {
			if (this.hasFullPermissions) {
				return true
			}
			return this.hasPermission(SEE_DATA_SUBJECT_PREFERENCE_HISTORY)
		},
		canSeeSubmissionHistory () {
			if (this.hasFullPermissions) {
				return true
			}
			return this.hasPermission(SEE_DATA_SUBJECT_SUBMISSION_HISTORY)
		},
		canEditPreferences () {
			if (this.hasFullPermissions) {
				return true
			}
			return this.hasPermission(EDIT_DATA_SUBJECT_CURRENT_PREFERENCES)
		},
		canEditContactDetails () {
			if (this.hasFullPermissions) {
				return true
			}
			return this.hasPermission(EDIT_DATA_SUBJECT_CONTACT_DETAILS)
		},
		showManageFooter () {
			return this.editContactDetails && this.masterCanEditContactDetails || this.editPreferences
		},
		hasAssociatedDevices () {
			return this.selectedDataSubject?.deviceAssociation?.length
		}
	},
	created () {
		this.loadDataSubjects()
	},
	methods: {
		async loadDataSubjects () {
			const responses = await Promise.all(
				this.allIds.map(id => getDataSubject(id, this.brandId))
			)
			const datasubjects = responses.map(({ data }) => data)
			for (const dataSubject of datasubjects) {
				const devices = await this.getAssociations(dataSubject.currentStandDak.syrenisId, this.brandId)
				dataSubject.deviceAssociation = devices.deviceAssociation
			}
			this.dataSubjects = datasubjects
			this.groupedProfileIds = this.getGroupedProfileSyrenisIds()
		},
		async getAssociations (syrenisId, brandId) {
			return (await getDataSubjectAssociations(syrenisId, brandId)).data
		},
		goToDataSubjectSearch () {
			this.$router.push({ name: DATA_SUBJECT_SEARCH })
		},
		getDataSubjectPreferences (dataSubject) {
			return [...(dataSubject?.currentPreferences || [])]
		},
		selectProfile ({ syrenisId }) {
			this.syrenisId = syrenisId
		},
		profilesByChannelId (channelId) {
			return this.dataSubjects.reduce((acc, dataSubject) => {
				const preferences = this.getDataSubjectPreferences(dataSubject)
				const preference = preferences.find(
					preference => preference.channelId === channelId
				)
				const profile = {}
				this.profileTableFields.forEach(({ fieldName, fieldLabel }) => {
					if (dataSubject.currentStandDak) {
						profile[fieldLabel] = dataSubject.currentStandDak[fieldName]
					}
				})
				if (preference) {
					acc.push({
						syrenisId: dataSubject.currentStandDak.syrenisId,
						profile,
						preference
					})
				}
				return acc
			}, [])
		},
		getCurrentSelection ({ isChannelOptedIn }) {
			return isChannelOptedIn ? OPTED_IN : OPTED_OUT
		},
		async onSaveContactDetails () {
			this.editDataSubject.submissionSource = 'Update Contact Details'
			await postDataSubject({ dataSubject: this.editDataSubject })
			await this.loadDataSubjects()
			this.showSnackbar('Your changes have been saved')
			this.editContactDetails = false
			this.editDataSubject = null
			this.historicPreferences = null
			this.submissionHistory = null
		},
		onEditContactDetails (resetValidation) {
			this.editDataSubject = JSON.parse(JSON.stringify(this.currentStandDak))
			resetValidation()
			this.editContactDetails = true
		},
		onCancelEditContactDetails (resetValidation) {
			resetValidation()
			this.editContactDetails = false
			this.editDataSubject = null
		},
		filterChannels (channel) {
			if (!this.editMode) {
				return channel.hasPreference === true || channel.hideNoPref === false
			}
			return channel
		},
		editPrefsIsInvalidSelection (value) {
			this.isInvalidSelection = value
		},
		submitPreferences (invalid) {
			if (invalid) return
			this.historicPreferences = null
			this.submissionHistory = null
			return this.$refs.dataSubjectPreferencesEdit.save()
		},
		getGroupedProfileSyrenisIds () {
			return this.dataSubjects
				.flatMap(dataSubject => dataSubject.currentGroupedStandDakRecords)
				.filter(record => record.groupTypeID === 2)
				.map(record => record.groupedSyrenisID)
		},
		async getDataSubjectHistory () {
			const { data: { preferences } } = await getDataSubjectHistory(this.syrenisId)
			this.historicPreferences = preferences.sort((a, b) => {
				return new Date(b.dateSubmitted) - new Date(a.dateSubmitted)
			})
		},
		async getDataSubjectSubmissions () {
			const { submissions } = await getDataSubjectSubmissions(this.syrenisId)
			const flattenedSubmissions = submissions.flatMap(ds => ds)
			this.submissionHistory = flattenedSubmissions.sort((a, b) => {
				return new Date(b.dateSubmitted) - new Date(a.dateSubmitted)
			})
		},
		async exportToPdf () {
			await this.getDataSubjectHistory()
			await this.getDataSubjectSubmissions()

			// eslint-disable-next-line new-cap
			const doc = new jsPDF()
			const pageMargin = 15
			const cellPadding = 2
			let yPos = 10 // Reduced from 20 to move logo closer to top

			// Calculate available page width for tables
			const pageWidth = doc.internal.pageSize.width
			const availableTableWidth = pageWidth - (pageMargin * 2)

			// Define proportional column widths for preference tables (in percentage)
			const columnWidths = [
				availableTableWidth * 0.15, // Brand (15%)
				availableTableWidth * 0.15, // Channel (15%)
				availableTableWidth * 0.12, // Preferences (12%)
				availableTableWidth * 0.17, // Consent Date (17%)
				availableTableWidth * 0.17, // Valid Until (17%)
				availableTableWidth * 0.12, // Source Code (12%)
				availableTableWidth * 0.12 // Source (12%)
			]

			// Header
			doc.addImage(SyrenisLogoBlack, pageMargin, yPos, 45, 15, '', 'FAST')
			yPos += 15 + 5 // image height (15) + space (5)

			doc.setFontSize(12)
			doc.text(`Audit Report for record ${this.syrenisId}`, pageMargin, yPos)
			yPos += 10

			// Add generation date
			doc.setFontSize(12)
			doc.text(`Generated on: ${formatUtcDate(new Date(Date.now()), DATE_TIME_FORMAT)}`, pageMargin, yPos)
			yPos += 10

			// Add note about UTC times
			doc.setFontSize(12)
			doc.text('All times shown are in UTC.', pageMargin, yPos)
			yPos += 5

			// Personal Details and Unique Identifiers
			doc.setLineWidth(0.5)
			const boxWidth = (doc.internal.pageSize.width - (pageMargin * 2) - 10) / 2

			// Get fields from configuration that should be shown
			const personalDetailsFields = this.contactDetailsFields.filter(field =>
				field.include === true && ['title', 'forename', 'middlename', 'surname', 'address1', 'address2',
					'address3', 'address4', 'address5', 'town', 'county', 'postcode', 'country',
					'email1', 'email2', 'telephone1', 'telephone2', 'mobile', 'organisationname', 'jobtitle'].includes(field.fieldName.toLowerCase())
			)

			const uniqueIdentifierFields = this.contactDetailsFields.filter(field =>
				field.include === true && ['urn1', 'urn2', 'urn3', 'custom1', 'custom2', 'custom3', 'custom4', 'custom5',
					'custom6', 'custom7', 'custom8', 'custom9', 'custom10'].includes(field.fieldName.toLowerCase())
			)

			// Calculate necessary heights based on number of fields
			const lineHeight = 5
			const headerSpace = 15 // Space for section header
			const minBoxHeight = 40 // Minimum box height

			// Calculate how many fields will actually be displayed (limited to 20 for practical reasons)
			// Only count fields that have include=true
			const personalDetailsCount = Math.min(personalDetailsFields.filter(field => field.include === true).length, 20)
			const uniqueIdentifiersCount = Math.min(uniqueIdentifierFields.filter(field => field.include === true).length, 20)

			// Calculate box heights (minimum 40px, or enough for headers + fields)
			const personalDetailsBoxHeight = Math.max(minBoxHeight, headerSpace + (personalDetailsCount * lineHeight) + 5)
			const uniqueIdentifiersBoxHeight = Math.max(minBoxHeight, headerSpace + (uniqueIdentifiersCount * lineHeight) + 5)

			// Use the taller box height for both boxes to maintain alignment
			const boxHeight = Math.max(personalDetailsBoxHeight, uniqueIdentifiersBoxHeight)

			// Draw boxes with dynamic heights
			doc.rect(pageMargin, yPos, boxWidth, boxHeight) // Personal Details Box
			doc.rect(pageMargin + boxWidth + 10, yPos, boxWidth, boxHeight) // Unique Identifiers Box

			// Personal Details Section
			doc.setFontSize(10)
			doc.setFont(undefined, 'bold')
			doc.text('Personal Details', pageMargin + 5, yPos + 7)
			doc.setFont(undefined, 'normal')

			let personalDetailsYPos = yPos + 15
			if (personalDetailsFields.length > 0) {
				personalDetailsFields.forEach((field, index) => {
					if (index < 20) { // Increased limit since we have dynamic height
						doc.text(`${field.fieldLabel || field.fieldName}: ${this.currentStandDak[field.fieldName] || '-'}`,
							pageMargin + 5, personalDetailsYPos)
						personalDetailsYPos += 5
					}
				})
			} else {
				doc.text(`No personal details configured to display`, pageMargin + 5, personalDetailsYPos)
			}

			// Unique Identifiers Section
			doc.setFontSize(10)
			doc.setFont(undefined, 'bold')
			doc.text('Unique Identifiers', pageMargin + boxWidth + 15, yPos + 7)
			doc.setFont(undefined, 'normal')

			let uniqueIdentifiersYPos = yPos + 15
			if (uniqueIdentifierFields.length > 0) {
				uniqueIdentifierFields.forEach((field, index) => {
					if (index < 20) { // Increased limit since we have dynamic height
						doc.text(`${field.fieldLabel || field.fieldName}: ${this.currentStandDak[field.fieldName] || '-'}`,
							pageMargin + boxWidth + 15, uniqueIdentifiersYPos)
						uniqueIdentifiersYPos += 5
					}
				})
			} else {
				doc.text(`No unique identifiers configured to display`, pageMargin + boxWidth + 15, uniqueIdentifiersYPos)
			}

			yPos += boxHeight + 5 // Space after the boxes

			// Current Preferences
			doc.setFontSize(12)
			doc.setFont(undefined, 'bold')
			doc.text('Current Preferences', pageMargin, yPos)
			yPos += 7
			const currentPreferencesHeaders = [['Brand', 'Channel', 'Preferences', 'Consent Date', 'Valid Until', 'Source Code', 'Source']]
			const currentPreferencesBody = []
			if (this.dataSubjectPreferences && this.dataSubjectPreferences.length > 0) {
				this.dataSubjectPreferences
					.slice() // Create a copy to sort without mutating the original
					.sort((a, b) => (a.channelName || '').localeCompare(b.channelName || ''))
					.forEach(preference => {
						currentPreferencesBody.push([
							preference.brandName || '-',
							preference.channelName || '-',
							preference.isChannelOptedIn ? 'Opted-In' : 'Opted-Out',
							preference.dateSubmittedUtc ? formatUtcDate(new Date(preference.dateSubmittedUtc)) : '-',
							preference.preferenceValidToUtc ? formatUtcDate(new Date(preference.preferenceValidToUtc)) : '-',
							preference.sourceCode || '-', // Source Code column
							preference.divID || '-' // Source column
						])
					})
			} else {
				currentPreferencesBody.push(['No current preferences', '', '', '', '', '', ''])
			}

			doc.autoTable({
				startY: yPos,
				head: currentPreferencesHeaders,
				body: currentPreferencesBody,
				margin: { left: pageMargin, right: pageMargin },
				styles: { cellPadding: cellPadding, fontSize: 8 },
				headStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: 'bold' },
				columnStyles: {
					0: { cellWidth: columnWidths[0] }, // Brand
					1: { cellWidth: columnWidths[1] }, // Channel
					2: { cellWidth: columnWidths[2] }, // Preferences
					3: { cellWidth: columnWidths[3] }, // Consent Date
					4: { cellWidth: columnWidths[4] }, // Valid Until
					5: { cellWidth: columnWidths[5] }, // Source Code
					6: { cellWidth: columnWidths[6] } // Source
				},
				didDrawPage: data => {
					yPos = data.cursor.y
				}
			})
			yPos = doc.autoTable.previous.finalY + 10

			// Historic Preferences
			doc.setFontSize(12)
			doc.setFont(undefined, 'bold')
			doc.text('Preference History', pageMargin, yPos)
			yPos += 7
			const historicPreferencesHeaders = [['Brand', 'Channel', 'Preferences', 'Consent Date', 'Valid Until', 'Source Code', 'Source']]
			const historicPreferencesBody = []
			if (this.historicPreferences && this.historicPreferences.length > 0) {
				this.historicPreferences
					.filter(preference => preference.isCurrent === false) // Filter for historic preferences
					.slice() // Create a copy to sort without mutating the original
					.sort((a, b) => (a.channelName || '').localeCompare(b.channelName || ''))
					.forEach(preference => {
						historicPreferencesBody.push([
							preference.brandName || '-',
							preference.channelName || '-',
							preference.isChannelOptedIn === 1 ? 'Opted-In' : 'Opted-Out',
							preference.dateSubmittedUtc
								? formatUtcDate(new Date(preference.dateSubmittedUtc))
								: '-',
							preference.validToUtc
								? formatUtcDate(new Date(preference.validToUtc), DATE_TIME_FORMAT)
								: '-',
							preference.sourceCode || '-',
							preference.divId || '-'
						])
					})
			} else {
				historicPreferencesBody.push(['No historic preferences found', '', '', '', '', '', ''])
			}
			doc.autoTable({
				startY: yPos,
				head: historicPreferencesHeaders,
				body: historicPreferencesBody,
				margin: { left: pageMargin, right: pageMargin },
				styles: { cellPadding: cellPadding, fontSize: 8 },
				headStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: 'bold' },
				columnStyles: {
					0: { cellWidth: columnWidths[0] }, // Brand
					1: { cellWidth: columnWidths[1] }, // Channel
					2: { cellWidth: columnWidths[2] }, // Preferences
					3: { cellWidth: columnWidths[3] }, // Consent Date
					4: { cellWidth: columnWidths[4] }, // Valid Until
					5: { cellWidth: columnWidths[5] }, // Source Code
					6: { cellWidth: columnWidths[6] } // Source
				},
				didDrawPage: data => {
					yPos = data.cursor.y
				}
			})
			yPos = doc.autoTable.previous.finalY + 10

			// Submission History
			doc.setFontSize(12)
			doc.setFont(undefined, 'bold')
			doc.text('Submission History', pageMargin, yPos)
			yPos += 7
			const submissionHistoryHeaders = [['Submission Type', 'Submission Date', 'Preferences Opted In', 'Preferences Opted Out']]
			const submissionHistoryBody = []

			// Calculate column widths for submission history table
			const submissionColumnWidths = [
				availableTableWidth * 0.25, // Submission Type (25%)
				availableTableWidth * 0.25, // Submission Date (25%)
				availableTableWidth * 0.25, // Preferences Opted In (25%)
				availableTableWidth * 0.25 // Preferences Opted Out (25%)
			]

			if (this.submissionHistory && this.submissionHistory.length > 0) {
				this.submissionHistory.forEach(submission => {
					// Count opted in and opted out preferences if they exist
					const optedInCount = submission.submittedPreferences?.filter(pref =>
						pref.newValue === 'Opted-In'
					).length || 0

					const optedOutCount = submission.submittedPreferences?.filter(pref =>
						pref.newValue === 'Opted-Out'
					).length || 0

					submissionHistoryBody.push([
						submission.submissionSource || submission.submissionType || '-',
						submission.dateSubmittedUtc ? formatUtcDate(new Date(submission.dateSubmittedUtc)) : '-',
						optedInCount.toString(),
						optedOutCount.toString()
					])
				})
			} else {
				submissionHistoryBody.push(['No submission history found', '', '', ''])
			}
			doc.autoTable({
				startY: yPos,
				head: submissionHistoryHeaders,
				body: submissionHistoryBody,
				margin: { left: pageMargin, right: pageMargin },
				styles: { cellPadding: cellPadding, fontSize: 8 },
				headStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: 'bold' },
				columnStyles: {
					0: { cellWidth: submissionColumnWidths[0] }, // Submission Type
					1: { cellWidth: submissionColumnWidths[1] }, // Submission Date
					2: { cellWidth: submissionColumnWidths[2] }, // Preferences Opted In
					3: { cellWidth: submissionColumnWidths[3] } // Preferences Opted Out
				},
				didDrawPage: data => {
					yPos = data.cursor.y
				}
			})
			yPos = doc.autoTable.previous.finalY + 10

			doc.save(`cassie-audit-report-${this.syrenisId}.pdf`)
			this.showSnackbar('PDF generated successfully')
		}
	}
}
</script>
